import React, { createContext, useContext, ReactNode } from 'react';
import { useChat } from '../hooks/chat/useChat';
import { useAuth } from './AuthContextZustand';
import { DISABLE_BACKEND_CHATBOT_CALLS } from '../services/chatConfig';
import type { Conversation, Message } from '../services/chatService';

interface ChatContextData {
  conversations: Conversation[];
  selectedConversation: Conversation | null;
  messages: Message[];
  waitingCount: number;
  loading: boolean;
  error: string | null;
  isTyping: boolean;
  loadConversations: (page?: number, limit?: number, status?: string) => Promise<void>;
  loadConversation: (conversationId: string) => Promise<void>;
  loadMessages: (conversationId: string, page?: number, limit?: number) => Promise<void>;
  loadWaitingCount: () => Promise<void>;
  assumeConversation: (conversationId: string) => Promise<void>;
  returnToBot: (conversationId: string) => Promise<void>;
  sendMessage: (conversationId: string, message: string) => Promise<void>;
  setSelectedConversation: React.Dispatch<React.SetStateAction<Conversation | null>>;
}

const ChatContext = createContext<ChatContextData>({} as ChatContextData);

interface ChatProviderProps {
  children: ReactNode;
}

const emptyChatData: ChatContextData = {
  conversations: [],
  selectedConversation: null,
  messages: [],
  waitingCount: 0,
  loading: false,
  error: null,
  isTyping: false,
  loadConversations: async () => {},
  loadConversation: async () => {},
  loadMessages: async () => {},
  loadWaitingCount: async () => {},
  assumeConversation: async () => {},
  returnToBot: async () => {},
  sendMessage: async () => {},
  setSelectedConversation: () => {},
};

export const ChatProvider: React.FC<ChatProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const chatData = useChat();

  const canUseChat =
    !DISABLE_BACKEND_CHATBOT_CALLS &&
    user?.permissions?.some((p) => ['iam.manage', 'appointments.read'].includes(p)) &&
    user?.establishmentWhatsAppEnabled === true;

  return (
    <ChatContext.Provider value={canUseChat ? chatData : emptyChatData}>
      {children}
    </ChatContext.Provider>
  );
};

export const useChatContext = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChatContext deve ser usado dentro de ChatProvider');
  }
  return context;
};
